// RevisionIssueRail
//
// The right-hand rail of the revision viewer's fullscreen mode.
//
// Everything else in the model-edits drawer answers "what changed". This
// answers "and what are we doing about it", without leaving the view:
//
//   Create issue  - captures the canvas, files a new issue on the
//                   project, and points that issue at THIS push and its
//                   diff (sourceModel.kind = 'commit', diffCommitId =
//                   the same push), so opening the issue re-opens the
//                   revision that raised it.
//   Close issue   - captures the canvas, files it as resolution evidence
//                   on an existing issue, and submits a resolution
//                   proposal carrying this push as the linked commit. On
//                   a project that does not gate proposals that resolves
//                   the issue outright; on one that does it lands in the
//                   owner's Review Center. The rail says which happened
//                   rather than claiming "resolved" either way.
//
// Both start by taking the picture, immediately, on the button press -
// the evidence is the view the reviewer was looking at when they decided,
// not the view left behind after they filled in a form. "Re-take" exists
// for when that is not what they wanted.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Input, InputNumber, Select, Alert, message, Tag, Tooltip, Spin } from "antd";
import { CameraOutlined, PlusOutlined, CheckCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { pruneFinishedIssues, useIssueListState } from "helpers/issueListPrune";
import {
  getIssues, createIssue, setIssueSourceModel, uploadIssueAttachments,
  uploadStandaloneScreenshot, submitIssueProposal,
} from "api/issue/issueApi";
import { issueLabel } from "helpers/artifactPrefix";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = {
  ink: "#23343E", text: "#3A4952", muted: "#8D8D8D", line: "#EFEFEF",
  bg: "#FAFAFA", wash: "#F3F7F9", navy: "#455D6B",
};

// An issue nobody has finished with. A resolved/abandoned one is
// not something you close again off the back of a revision.
const CLOSEABLE = ["open", "pending", "under_review"];

// How many changed elements ride along on a create/resolve. The endpoints
// cap their own storage (5000 on the create snapshot); this keeps the
// request small on a push that rewrote a whole floor.
const ELEMENT_CAP = 400;

function dataUrlToFile(dataUrl, name) {
  const m = /^data:(image\/[a-z+]+);base64,(.+)$/i.exec(dataUrl || "");
  if (!m) return null;
  const bin = atob(m[2]);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return new File([bytes], name, { type: m[1] });
}

function Section({ title, children, right }) {
  return (
    <div style={{ borderBottom: `1px solid ${T.line}`, padding: "12px 14px", background: "#fff" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, letterSpacing: ".06em" }}>{title}</span>
        <span style={{ flex: 1 }} />
        {right}
      </div>
      {children}
    </div>
  );
}

function ShotPreview({ src, onRetake }) {
  if (!src) return null;
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{
        border: `1px solid ${T.line}`, borderRadius: 6, overflow: "hidden",
        background: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <img src={src} alt="Captured view" style={{ maxWidth: "100%", display: "block" }} />
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5 }}>
        <span style={{ fontSize: 11, color: T.muted, flex: 1 }}>The view as it was when you pressed the button.</span>
        <Button size="small" type="text" icon={<ReloadOutlined />} onClick={onRetake}>Re-take</Button>
      </div>
    </div>
  );
}

/**
 * @param {object}   props
 * @param {string}   props.projectId
 * @param {string}   props.commitId       the push being looked at
 * @param {string}   props.commitLabel
 * @param {string}   props.projectTitle
 * @param {Array}    props.fragments      edit-fragments rows (for element ids)
 * @param {Array}    props.forIssues      issues this push was pushed for
 * @param {Function} props.capture        () => dataURL of the live canvas
 * @param {boolean}  props.canCapture     the push has drawable geometry at all
 * @param {boolean}  props.ready          the scene has geometry to capture
 * @param {string}   props.action         '' | 'create' | 'close' (controlled)
 * @param {Function} props.onAction
 */
export default function RevisionIssueRail({
  projectId, commitId, commitLabel, projectTitle,
  fragments = [], forIssues = [], capture, canCapture = true, ready = false,
  action = "", onAction,
}) {
  const [shot, setShot] = useState(null);            // data URL
  const [busy, setBusy] = useState(false);
  const [issues, setIssues] = useState([]);
  const [loadingIssues, setLoadingIssues] = useState(false);
  const [done, setDone] = useState(null);            // { kind, label, id, pending }

  // Create form
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("normal");
  // Required on every new issue (owner, 2026-10-16).
  const [estimate, setEstimate] = useState(null);
  // Close form
  const [issueId, setIssueId] = useState("");
  const [note, setNote] = useState("");
  const { finished, reopened } = useIssueListState();

  // The project's unfinished issues, so "close issue" has something to
  // pick from beyond the ones this push declared itself to be for.
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    setLoadingIssues(true);
    getIssues({ projectId, limit: 200, sort: "updatedAt", sort_dir: -1, slim: true })
      .then((r) => { if (!cancelled) setIssues(Array.isArray(r?.issues) ? r.issues : []); })
      .catch(() => { if (!cancelled) setIssues([]); })
      .finally(() => { if (!cancelled) setLoadingIssues(false); });
    return () => { cancelled = true; };
  }, [projectId]);

  // forIssues comes off the commit itself and may not be in the page of
  // issues the list endpoint returned - it goes first either way.
  const closeable = useMemo(() => {
    const seen = new Set();
    const rows = [];
    const push = (i, linked) => {
      const id = String(i._id || "");
      if (!id || seen.has(id)) return;
      if (!CLOSEABLE.includes(i.status)) return;
      seen.add(id);
      rows.push({ ...i, linked });
    };
    forIssues.forEach((i) => push(i, true));
    issues.forEach((i) => push(i, false));
    // An issue finished elsewhere in this session is no longer closeable,
    // whatever the status on the copy this rail fetched (issueListPrune).
    return pruneFinishedIssues(rows, finished, reopened);
  }, [forIssues, issues, finished, reopened]);

  useEffect(() => {
    if (!issueId && closeable.length) setIssueId(String(closeable[0]._id));
  }, [closeable, issueId]);

  // Returns null when there is nothing on the canvas. That is not an
  // error for "create" - a push that changed nothing still deserves an
  // issue - so the complaint is only raised where a picture is required.
  const take = useCallback(() => {
    const d = capture && capture();
    if (!d) return null;
    setShot(d);
    return d;
  }, [capture]);

  const start = useCallback((kind) => {
    setDone(null);
    const d = take();
    if (!d && kind === "close") {
      message.error("This push has no 3D view to photograph, and a resolution needs one.");
      return;
    }
    onAction && onAction(kind);
  }, [take, onAction]);

  const cancel = () => { onAction && onAction(""); setShot(null); };

  // A form armed from outside (the revision row's "Create issue" button)
  // opens before the fragments have finished streaming, so there is
  // nothing to photograph at that moment. Take the picture the instant
  // there is - the reviewer still gets a shot they never had to ask for.
  useEffect(() => {
    if (!action || shot || !ready) return;
    const d = capture && capture();
    if (d) setShot(d);
  }, [action, shot, ready, capture]);

  // The changed elements, in the shape both endpoints want. A create
  // stores them as the issue's "before" snapshot (which is what makes
  // auto-detect resolve work later); a resolution submits them as the
  // elements the fix touched.
  const elementRows = useMemo(() => fragments.slice(0, ELEMENT_CAP).map((f) => {
    const rec = (f.new && f.new.record) || (f.old && f.old.record) || {};
    return {
      uniqueId: f.uniqueId,
      fragmentHash: rec.fragmentHash || "",
      tileKey: rec.tileKey || "",
      category: f.category || "",
      level: f.level || "",
    };
  }).filter((e) => e.uniqueId), [fragments]);

  const uploadShot = async (dataUrl, name) => {
    const file = dataUrlToFile(dataUrl, name);
    if (!file) throw new Error("The capture could not be encoded");
    const up = await uploadStandaloneScreenshot(file);
    if (!up || !up.url) throw new Error("The screenshot did not upload");
    return up.url;
  };

  const doCreate = async () => {
    if (!title.trim()) { message.warning("Give the issue a title"); return; }
    if (!(Number(estimate) > 0)) { message.warning("Enter the estimated hours"); return; }
    // A picture if there is one to take; the issue is filed either way,
    // and it still points at the push.
    const dataUrl = shot || take();
    setBusy(true);
    try {
      const screenshot = dataUrl
        ? await uploadShot(dataUrl, `revision-${commitId}.png`)
        : "";
      const created = await createIssue({
        title: title.trim(),
        description: description.trim(),
        projectId,
        priority,
        estimatedHours: Number(estimate),
        createdFrom: "web",
        sourceType: "3d",
        ...(screenshot ? { sourceModelViewer: { screenshot } } : {}),
        // Freeze what the revision showed as the issue's "before".
        openedAtSnapshotElements: elementRows,
      });
      const issue = created?.data || created?.issue || created;
      const newId = issue?._id;
      if (!newId) throw new Error("The issue was created but came back without an id");

      // Point it at this push AND its diff, so opening the issue opens
      // the revision that raised it rather than "the model, today".
      let pinned = true;
      try {
        await setIssueSourceModel(newId, { commitId });
      } catch (e) {
        // A push that predates versioned exports has no geometry left to
        // stream. The issue is still perfectly good - say what it lost.
        pinned = false;
      }
      setDone({
        kind: "created", id: newId,
        label: issueLabel(issue) || "the issue",
        note: pinned ? "" : "The push has no versioned export left, so the issue could not be pinned to it - the screenshot is still attached.",
      });
      onAction && onAction("");
      setTitle(""); setDescription(""); setShot(null); setEstimate(null);
      message.success("Issue created from this revision");
    } catch (e) {
      message.error(e?.message || "The issue could not be created");
    } finally {
      setBusy(false);
    }
  };

  const doClose = async () => {
    if (!issueId) { message.warning("Pick the issue this revision closes"); return; }
    const dataUrl = shot || take();
    if (!dataUrl) {
      message.error("This push has no 3D view to photograph, and a resolution needs one.");
      return;
    }
    // A resolution carries the hours spent (back/utils/issueHours).
    const { askHoursSpent } = await import("components/Issue/HoursSpent");
    const hoursSpent = await askHoursSpent({ title: "Hours spent on this issue", okText: "Close the issue" });
    if (hoursSpent === null) return;
    setBusy(true);
    try {
      const file = dataUrlToFile(dataUrl, `resolution-${commitId}.png`);
      if (!file) throw new Error("The capture could not be encoded");
      // Filed as resolution evidence (role "resolution"), which is what
      // puts it in Document Control under "Resolution screenshot".
      const up = await uploadIssueAttachments(issueId, [file], { role: "resolution" });
      const added = (up && up.added) || [];
      const evidence = added.map((a) => a._id).filter(Boolean);
      const shots = added.filter((a) => a.kind === "image").map((a) => a.url).filter(Boolean);

      const resp = await submitIssueProposal(issueId, {
        commitId,
        // The push IS the fix, so the elements it changed are the
        // elements resolved.
        resolvedElements: elementRows.length
          ? elementRows.map((e, i) => ({ ...e, screenshotUrl: i === 0 ? shots[0] : undefined }))
          : [{ uniqueId: "thumbnail-only", screenshotUrl: shots[0] }],
        afterFragmentHashes: elementRows.map((e) => e.fragmentHash).filter(Boolean),
        notes: note.trim() || `Resolved from revision ${commitLabel || commitId}`,
        submissionChannel: "web",
        elementPickMode: "auto",
        screenshotUrls: shots,
        evidence,
        hoursSpent,
      });
      const picked = closeable.find((i) => String(i._id) === String(issueId));
      setDone({
        kind: "closed", id: issueId,
        label: picked ? (issueLabel(picked) || picked.title) : "the issue",
        pending: !resp?.autoApproved,
      });
      onAction && onAction("");
      setNote(""); setShot(null);
      message.success(resp?.autoApproved
        ? "Issue resolved - the screenshot is filed as evidence"
        : "Sent to the Review Center - it counts as resolved once approved");
    } catch (e) {
      message.error(e?.message || "The issue could not be resolved");
    } finally {
      setBusy(false);
    }
  };

  // Backend/notification links address issues by _id; /issue-viewer
  // resolves the id and redirects to the real /issue/:shareToken page.
  const issueHref = (id) => `/issue-viewer/${id}`;

  return (
    <div style={{
      width: 340, flex: "0 0 340px", borderLeft: `1px solid ${T.line}`,
      background: T.bg, display: "flex", flexDirection: "column", minHeight: 0, overflow: "auto",
    }}>
      <div style={{ padding: "12px 14px", background: "#fff", borderBottom: `1px solid ${T.line}` }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>Act on this revision</div>
        <div style={{ fontSize: 11.5, color: T.muted, marginTop: 3, lineHeight: 1.45 }}>
          {canCapture ? "Both actions photograph the 3D view as it stands and file it against"
                      : "Raise an issue against"}{" "}
          {commitLabel ? <strong style={{ color: T.text }}>{commitLabel}</strong> : "this push"}
          {projectTitle ? ` on ${projectTitle}` : ""}.
        </div>
      </div>

      {done && (
        <div style={{ padding: "10px 14px", background: "#fff", borderBottom: `1px solid ${T.line}` }}>
          <Alert
            type={done.pending ? "info" : "success"}
            showIcon
            message={done.kind === "created"
              ? `Issue ${done.label} created`
              : done.pending
                ? `${done.label} sent to the Review Center`
                : `${done.label} resolved`}
            description={
              <span style={{ fontSize: 12 }}>
                {done.note ? `${done.note} ` : ""}
                <a href={issueHref(done.id)} target="_blank" rel="noopener noreferrer">Open it</a>
              </span>
            }
          />
        </div>
      )}

      {action === "" && (
        <Section title="ISSUE ACTIONS">
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <Button type="primary" icon={<PlusOutlined />} block onClick={() => start("create")}>
              {canCapture ? "Create issue from this view" : "Create issue against this push"}
            </Button>
            <Tooltip title={
              !canCapture ? "This push has no drawable geometry, so there is no view to submit as proof of the fix"
                : closeable.length ? "" : "No open issue on this project to close"
            }>
              <Button
                icon={<CheckCircleOutlined />} block
                disabled={!canCapture || (!closeable.length && !loadingIssues)}
                onClick={() => start("close")}
              >
                Close an issue with this view
              </Button>
            </Tooltip>
          </div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
            {canCapture ? (
              <><CameraOutlined /> Pressing either one takes the screenshot straight away.</>
            ) : (
              "This push has nothing to draw, so there is no screenshot to take. "
              + "The issue still points at the push and its diff."
            )}
          </div>
        </Section>
      )}

      {action === "create" && (
        <Section
          title="NEW ISSUE"
          right={<Button size="small" type="text" onClick={cancel}>Cancel</Button>}
        >
          <ShotPreview src={shot} onRetake={take} />
          <Input
            placeholder="What is wrong?"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ marginBottom: 8 }}
            maxLength={200}
          />
          <Input.TextArea
            placeholder="Anything the picture does not say (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoSize={{ minRows: 3, maxRows: 8 }}
            style={{ marginBottom: 8 }}
          />
          <InputNumber
            aria-label="Estimated hours"
            placeholder="Estimated hours (required)"
            min={0.25} step={0.25} precision={2}
            value={estimate}
            onChange={setEstimate}
            addonAfter="hours"
            style={{ width: "100%", marginBottom: 8 }}
          />
          <Select
            value={priority}
            onChange={setPriority}
            style={{ width: "100%", marginBottom: 10 }}
            options={[
              { value: "urgent", label: "Urgent" },
              { value: "normal", label: "Normal" },
              { value: "low", label: "Low" },
            ]}
          />
          <Button type="primary" block loading={busy} onClick={doCreate}>
            Create issue
          </Button>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
            The issue opens on this push and its diff
            {elementRows.length
              ? `, with ${elementRows.length} changed element${elementRows.length === 1 ? "" : "s"} recorded as its starting state.`
              : ". This push changed no elements, so there is no starting state to record."}
          </div>
        </Section>
      )}

      {action === "close" && (
        <Section
          title="CLOSE AN ISSUE"
          right={<Button size="small" type="text" onClick={cancel}>Cancel</Button>}
        >
          <ShotPreview src={shot} onRetake={take} />
          {loadingIssues ? (
            <div style={{ padding: 12, textAlign: "center" }}><Spin size="small" /></div>
          ) : !closeable.length ? (
            <Alert type="info" showIcon message="Nothing to close" description="This project has no open issue." />
          ) : (
            <>
              <Select filterOption={matchOption}
                value={issueId}
                onChange={setIssueId}
                showSearch
                // antd 5 filters on the VALUE unless told otherwise, and
                // these values are ObjectIds - typing an issue's name
                // would match nothing.
                optionFilterProp="label"
                style={{ width: "100%", marginBottom: 8 }}
                options={closeable.map((i) => ({
                  value: String(i._id),
                  label: `${issueLabel(i) ? `${issueLabel(i)} · ` : ""}${i.title || "Untitled"}`,
                }))}
              />
              {(() => {
                const picked = closeable.find((i) => String(i._id) === String(issueId));
                return picked && picked.linked ? (
                  <Tag color="blue" style={{ marginBottom: 8 }}>this push was made for it</Tag>
                ) : null;
              })()}
              <Input.TextArea
                placeholder="What the push fixed (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                autoSize={{ minRows: 3, maxRows: 8 }}
                style={{ marginBottom: 10 }}
              />
              <Button type="primary" block loading={busy} onClick={doClose}>
                Submit screenshot &amp; close
              </Button>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 8, lineHeight: 1.5 }}>
                The screenshot is filed as resolution evidence and this push is
                linked as the fix. On a project that requires approval this goes
                to the Review Center first.
              </div>
            </>
          )}
        </Section>
      )}
    </div>
  );
}
